import { throttle } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'

import FaqAccordion from 'components/FaqAccordion'
import SectionWrapper from 'components/SectionWrapper'
import { useSwitcher } from 'hooks/saveElementIndex'
import { Col, Row } from 'styles/components/layout'

import FaqBlockArrow from './components/FaqBlockArrow'
import FaqBlockContent from './components/FaqBlockContent'
import FaqBlockTab from './components/FaqBlockTab'
import { Wrapper, Answers, H2Styled, ColStyled } from './styles'

/**
 * Faq block component. You can use it on product pages.
 *
 * @return {Component} Component — FaqBlock
 */
function FaqBlock({
  colorTheme,
  wrapperClassName,
  title,
  locale,
  isMobile,
  items,
}) {
  const { index, updateIndex } = useSwitcher()
  const [arrowOffsetTop, setArrowOffsetTop] = React.useState(0)
  const [allTabs, setAllTabs] = React.useState([])
  const arrowRef = React.useRef()
  const containerRef = React.useRef()

  const calculateArrowPosition = React.useCallback(() => {
    if (isMobile) {
      return false
    }
    const containerOffsetTop =
      (containerRef.current?.getBoundingClientRect()?.top || 0) + pageYOffset
    if (allTabs.length) {
      const { top: activeTabOffsetTop = 0, height: activeTabHeight = 0 } =
        allTabs[index].current?.getBoundingClientRect() || {}
      const arrowHeight = arrowRef.current?.getBoundingClientRect()?.height || 0
      const arrowOffsetTop = activeTabOffsetTop + pageYOffset
      const arrowMiddlePos =
        activeTabHeight - activeTabHeight / 2 - arrowHeight / 2
      setArrowOffsetTop(arrowOffsetTop - containerOffsetTop + arrowMiddlePos)
    }
  }, [isMobile, index, allTabs])

  const handleChange = itemIndex => {
    if (itemIndex === null) {
      updateIndex(0)
    } else if (index !== itemIndex) {
      updateIndex(itemIndex)
    }
  }

  const renderFaqBlock = () => (
    <Row ref={containerRef}>
      <Col width={{ md: 4 / 12, lg: 5 / 12 }}>
        {title && (
          <H2Styled
            as={'h3'}
            {...{ colorTheme }}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        )}
        {items.map((item, i, arr) => (
          <FaqBlockTab
            isLast={i === arr.length - 1}
            colorTheme={colorTheme}
            tabRef={allTabs[i]}
            isActive={index === i}
            key={i}
            title={item.title}
            onTabClick={() => updateIndex(i)}
          />
        ))}
      </Col>
      <Col width={{ xs: 1 / 12 }} />
      <ColStyled width={{ md: 7 / 12, lg: 6 / 12 }}>
        <FaqBlockArrow
          {...{ colorTheme }}
          ref={arrowRef}
          top={arrowOffsetTop}
        />
        <Answers {...{ colorTheme }}>
          <FaqBlockContent
            item={items[index]}
            activeIndex={index}
            locale={locale}
          />
        </Answers>
      </ColStyled>
    </Row>
  )

  React.useEffect(() => {
    setAllTabs(items.map(() => React.createRef()))
  }, [items])

  React.useEffect(() => {
    const throttledCalculateArrowPosition = throttle(
      calculateArrowPosition,
      100,
    )
    calculateArrowPosition()
    window.addEventListener('resize', throttledCalculateArrowPosition)

    return () => {
      window.removeEventListener('resize', throttledCalculateArrowPosition)
    }
  }, [calculateArrowPosition])

  return (
    <SectionWrapper classNameWrapper={wrapperClassName}>
      {isMobile ? (
        <>
          {title && (
            <H2Styled
              as={'h3'}
              {...{ colorTheme }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          )}
          <Wrapper>
            <FaqAccordion
              {...{ colorTheme, locale, items }}
              onChange={handleChange}
              openedItemIndex={index}
            />
          </Wrapper>
        </>
      ) : (
        renderFaqBlock()
      )}
    </SectionWrapper>
  )
}

FaqBlock.propTypes = {
  /** Items array for render */
  items: PropTypes.array.isRequired,

  /** Flag to check mobile device or not */
  isMobile: PropTypes.bool,

  /** Current locale */
  locale: PropTypes.string,

  /** Title of the faq block */
  title: PropTypes.string,

  /** Class for wrapper component */
  wrapperClassName: PropTypes.string,

  /** Color theme of the faq block */
  colorTheme: PropTypes.oneOf(['dark', 'light']),
}

FaqBlock.defaultProps = {
  isMobile: false,
  title: '',
  wrapperClassName: '',
  colorTheme: 'light',
}

export default FaqBlock
